import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  Save, Lock, ShieldCheck, ShieldOff, Loader2, RefreshCw,
  Building2, CreditCard, Banknote, Settings2, CheckCircle2, CalendarDays,
} from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { getAllConfig, updateConfig, getTotpSetup, confirmTotpSetup, type ConfigRow, type TotpSetup } from '@/api/config'
import { ApiError } from '@/lib/api-error'
import { TotpVerifyModal } from '@/components/shared/TotpVerifyModal'

const GROUP_META: Record<string, { label: string; icon: React.ReactNode; description: string }> = {
  business: {
    label: 'Negocio',
    icon: <Building2 className="w-4 h-4" />,
    description: 'Nombre, logo y datos del negocio que aparecen en la app',
  },
  plan: {
    label: 'Plan contratado',
    icon: <CreditCard className="w-4 h-4" />,
    description: 'Límites de recursos y configuración del plan activo',
  },
  prices: {
    label: 'Precios del plan',
    icon: <Banknote className="w-4 h-4" />,
    description: 'Tarifas base y adicionales por recurso',
  },
  billing: {
    label: 'Facturación',
    icon: <CalendarDays className="w-4 h-4" />,
    description: 'Fecha de inicio de facturación y día de corte mensual',
  },
}

const PRICE_KEYS       = ['price_base_cop', 'price_extra_device_cop', 'price_extra_members_block_cop', 'price_extra_user_cop']
const PLAN_LIMIT_KEYS  = ['plan_limit_devices', 'plan_limit_members', 'plan_limit_system_users']

function formatCOP(value: string) {
  const n = parseInt(value, 10)
  return isNaN(n) ? value : `$${n.toLocaleString('es-CO')} COP`
}

// ── Price summary table ───────────────────────────────────────────────────────

function PriceTable({ rows }: { rows: ConfigRow[] }) {
  const byKey    = Object.fromEntries(rows.map(r => [r.key, r.value]))
  const limitMap = Object.fromEntries(rows.filter(r => PLAN_LIMIT_KEYS.includes(r.key)).map(r => [r.key, r.value]))

  const items = [
    {
      label: 'Plan base',
      detail: `${limitMap['plan_limit_devices'] ?? '–'} disp. · ${limitMap['plan_limit_members'] ?? '–'} miembros · ${limitMap['plan_limit_system_users'] ?? '–'} admin`,
      price: byKey['price_base_cop'] ?? '0',
      suffix: '/mes',
      highlight: true,
    },
    { label: '+ Dispositivo extra',    detail: 'por dispositivo', price: byKey['price_extra_device_cop'] ?? '0',          suffix: '/mes', highlight: false },
    { label: '+ 200 miembros extra',   detail: 'por bloque',     price: byKey['price_extra_members_block_cop'] ?? '0',   suffix: '/mes', highlight: false },
    { label: '+ Usuario admin extra',  detail: 'por usuario',    price: byKey['price_extra_user_cop'] ?? '0',            suffix: '/mes', highlight: false },
  ]

  return (
    <div className="bg-background border border-border rounded-xl overflow-hidden mb-5">
      <div className="px-4 py-2.5 border-b border-border bg-surface/50">
        <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest">Resumen de tarifas actuales</p>
      </div>
      <div className="divide-y divide-border">
        {items.map(item => (
          <div key={item.label} className={`flex items-center justify-between gap-4 px-4 py-3 ${item.highlight ? 'bg-accent/5' : ''}`}>
            <div className="min-w-0">
              <p className={`text-sm font-semibold leading-tight ${item.highlight ? 'text-text-primary' : 'text-text-muted'}`}>{item.label}</p>
              <p className="text-text-muted text-[10px] mt-0.5">{item.detail}</p>
            </div>
            <p className={`text-sm font-bold shrink-0 tabular-nums ${item.highlight ? 'text-accent' : 'text-text-primary'}`}>
              {formatCOP(item.price)}<span className="text-text-muted font-normal text-xs">{item.suffix}</span>
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Config field ──────────────────────────────────────────────────────────────

function ConfigField({ row, value, error, onChange }: {
  row: ConfigRow; value: string; error?: string; onChange: (key: string, value: string) => void
}) {
  if (!row.editable) {
    return (
      <div className="flex items-center justify-between gap-4 py-3 border-b border-border/50 last:border-0">
        <div className="flex items-center gap-2 min-w-0">
          <Lock className="w-3 h-3 text-text-muted/50 shrink-0" />
          <span className="text-text-muted text-sm truncate">{row.label}</span>
        </div>
        <span className="text-text-primary text-xs font-mono bg-background border border-border px-2.5 py-1 rounded-lg shrink-0">
          {value}
        </span>
      </div>
    )
  }

  if (row.type === 'boolean') {
    const isOn = value === 'true'
    return (
      <div className="flex items-center justify-between gap-4 py-3 border-b border-border/50 last:border-0">
        <div className="min-w-0">
          <p className="text-text-primary text-sm font-medium">{row.label}</p>
        </div>
        <button
          onClick={() => onChange(row.key, isOn ? 'false' : 'true')}
          className={`relative w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none shrink-0 cursor-pointer ${isOn ? 'bg-accent' : 'bg-border'}`}
          role="switch"
          aria-checked={isOn}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${isOn ? 'translate-x-6' : 'translate-x-0'}`} />
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1.5 py-3 border-b border-border/50 last:border-0">
      <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">{row.label}</label>
      {row.type === 'integer' ? (
        <div className="flex items-center bg-background border border-border rounded-xl px-4 py-2.5 gap-2 focus-within:ring-2 focus-within:ring-accent/30 focus-within:border-accent/50 transition-all">
          {PRICE_KEYS.includes(row.key) && <span className="text-text-muted text-sm font-semibold shrink-0">$</span>}
          <input
            type="number"
            value={value}
            onChange={e => onChange(row.key, e.target.value)}
            className="flex-1 bg-transparent text-text-primary text-sm font-semibold focus:outline-none tabular-nums min-w-0"
          />
          {PRICE_KEYS.includes(row.key) && <span className="text-text-muted text-xs font-medium shrink-0">COP</span>}
        </div>
      ) : (
        <input
          type="text"
          value={value}
          onChange={e => onChange(row.key, e.target.value)}
          className={`w-full bg-background border rounded-xl px-4 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/50 transition-all ${error ? 'border-red-500/60' : 'border-border'}`}
        />
      )}
      {error && <p className="text-red-500 text-xs">{error}</p>}
    </div>
  )
}

// ── TOTP Section ──────────────────────────────────────────────────────────────

function TotpSection({ confirmed, onConfirmed }: { confirmed: boolean; onConfirmed: () => void }) {
  const [setup, setSetup]               = useState<TotpSetup | null>(null)
  const [loadingQr, setLoadingQr]       = useState(false)
  const [confirmCode, setConfirmCode]   = useState('')
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [confirming, setConfirming]     = useState(false)
  const [done, setDone]                 = useState(false)

  async function handleStartSetup() {
    setLoadingQr(true); setSetup(null); setConfirmCode(''); setConfirmError(null)
    try {
      setSetup(await getTotpSetup())
    } catch {
      toast.error('Error al generar el QR. Intenta de nuevo.')
    } finally {
      setLoadingQr(false)
    }
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault()
    if (confirmCode.length !== 6 || confirming) return
    setConfirming(true); setConfirmError(null)
    try {
      await confirmTotpSetup(confirmCode)
      setDone(true); setSetup(null)
      toast.success('Autenticador configurado correctamente')
      onConfirmed()
    } catch (err) {
      setConfirmError(err instanceof ApiError ? err.message : 'Error al confirmar')
      setConfirmCode('')
    } finally {
      setConfirming(false)
    }
  }

  const isConfigured = confirmed || done

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${isConfigured ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-amber-500/10 border border-amber-500/20'}`}>
          {isConfigured
            ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
            : <ShieldOff   className="w-3.5 h-3.5 text-amber-500" />}
        </div>
        <h2 className="text-text-muted text-xs font-bold uppercase tracking-widest">Seguridad</h2>
      </div>

      <div className="bg-surface border border-border rounded-2xl overflow-hidden">
        {isConfigured && !setup ? (
          <div className="p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-text-primary text-sm font-bold leading-tight">Autenticador configurado</p>
                <p className="text-text-muted text-xs mt-0.5">Las operaciones sensibles requieren verificación TOTP</p>
              </div>
            </div>
            <span className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full shrink-0">
              <CheckCircle2 className="w-3 h-3" /> Activo
            </span>
          </div>
        ) : !setup ? (
          <div className="p-5 flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                <ShieldOff className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-text-primary text-sm font-bold leading-tight">Sin autenticador</p>
                <p className="text-text-muted text-xs mt-0.5">Configura TOTP para proteger operaciones sensibles del sistema</p>
              </div>
            </div>
            <button
              onClick={handleStartSetup}
              disabled={loadingQr}
              className="flex items-center gap-2 px-4 py-2.5 bg-accent text-background text-sm font-bold rounded-xl hover:bg-accent-hover transition-colors disabled:opacity-50 cursor-pointer shrink-0"
            >
              {loadingQr ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
              Configurar
            </button>
          </div>
        ) : (
          <div className="p-5 flex flex-col gap-5">
            <p className="text-text-muted text-sm leading-relaxed">
              Escanea este QR con tu app autenticadora (Google Authenticator, Authy…), luego ingresa el código de 6 dígitos para confirmar.
            </p>
            <div className="flex justify-center">
              <div className="p-3 bg-white rounded-2xl border border-border shadow-sm">
                <img
                  src={`data:image/png;base64,${setup.qr_base64}`}
                  alt="QR Code TOTP"
                  className="w-44 h-44"
                />
              </div>
            </div>
            <form onSubmit={handleConfirm} className="flex flex-col gap-3">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={confirmCode}
                onChange={e => { setConfirmCode(e.target.value.replace(/\D/g, '')); setConfirmError(null) }}
                placeholder="000000"
                className={`w-full bg-background border rounded-xl px-4 py-3 text-center text-2xl font-mono font-bold text-text-primary tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all ${confirmError ? 'border-red-500/60' : 'border-border'}`}
              />
              {confirmError && <p className="text-red-500 text-xs text-center">{confirmError}</p>}
              <button
                type="submit"
                disabled={confirmCode.length !== 6 || confirming}
                className="flex items-center justify-center gap-2 py-2.5 bg-accent text-background text-sm font-bold rounded-xl hover:bg-accent-hover transition-colors disabled:opacity-50 cursor-pointer"
              >
                {confirming && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {confirming ? 'Verificando…' : 'Confirmar configuración'}
              </button>
            </form>
          </div>
        )}

        {/* Reconfigurar (cuando ya está activo y se ha escaneado) */}
        {isConfigured && !setup && (
          <div className="border-t border-border px-5 py-3 flex justify-end">
            <button
              onClick={handleStartSetup}
              disabled={loadingQr}
              className="flex items-center gap-1.5 text-xs font-medium text-text-muted hover:text-text-primary transition-colors disabled:opacity-50 cursor-pointer"
            >
              {loadingQr ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Reconfigurar autenticador
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export function SystemConfig() {
  const [rows, setRows]             = useState<ConfigRow[]>([])
  const [values, setValues]         = useState<Record<string, string>>({})
  const [errors, setErrors]         = useState<Record<string, string>>({})
  const [savingGroup, setSavingGroup] = useState<string | null>(null)
  const [loading, setLoading]       = useState(true)
  const [totpConfirmed, setTotpConfirmed] = useState(false)
  const [totpModal, setTotpModal]   = useState<{ group: string } | null>(null)

  useEffect(() => {
    getAllConfig()
      .then(({ config }) => {
        setRows(config)
        setValues(Object.fromEntries(config.map(r => [r.key, r.value])))
        const confirmed = config.find(r => r.key === 'totp_confirmed')
        setTotpConfirmed(confirmed?.value === 'true')
      })
      .catch(() => toast.error('Error al cargar la configuración'))
      .finally(() => setLoading(false))
  }, [])

  function handleChange(key: string, value: string) {
    setValues(prev => ({ ...prev, [key]: value }))
    setErrors(prev => { const next = { ...prev }; delete next[key]; return next })
  }

  async function doSaveGroup(group: string, opToken?: string) {
    const groupRows = rows.filter(r => r.group === group && r.editable)
    const updates   = Object.fromEntries(groupRows.map(r => [r.key, values[r.key] ?? r.value]))
    setSavingGroup(group)
    try {
      await updateConfig(updates, opToken)
      toast.success('Configuración guardada')
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) {
        const body = e as unknown as { errors?: Record<string, string> }
        if (body.errors) setErrors(prev => ({ ...prev, ...body.errors }))
        toast.error('Corrige los errores antes de guardar')
      } else {
        toast.error(e instanceof ApiError ? e.message : 'Error al guardar')
      }
    } finally {
      setSavingGroup(null)
    }
  }

  function handleSaveGroup(group: string) {
    totpConfirmed ? setTotpModal({ group }) : doSaveGroup(group)
  }

  function handleTotpVerified(opToken: string) {
    const group = totpModal?.group
    setTotpModal(null)
    if (group) doSaveGroup(group, opToken)
  }

  const groups = [...new Set(rows.filter(r => r.group !== 'security').map(r => r.group))].sort()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-text-primary font-bold text-xl leading-tight">Configuración del sistema</h1>
          <p className="text-text-muted text-xs mt-1">Ajustes globales, plan, precios y seguridad</p>
        </div>
        <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/15 flex items-center justify-center shrink-0">
          <Settings2 className="w-5 h-5 text-accent" />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="seguridad">
        <TabsList className="bg-surface border border-border h-auto p-1 rounded-xl gap-0.5">
          <TabsTrigger value="seguridad" className="rounded-lg px-4 py-2 text-xs font-semibold text-text-muted data-[state=active]:bg-background data-[state=active]:text-text-primary data-[state=active]:shadow-sm transition-all">
            Seguridad
          </TabsTrigger>
          {groups.map(group => (
            <TabsTrigger key={group} value={group} className="rounded-lg px-4 py-2 text-xs font-semibold text-text-muted data-[state=active]:bg-background data-[state=active]:text-text-primary data-[state=active]:shadow-sm transition-all">
              {GROUP_META[group]?.label ?? group}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ── Seguridad ── */}
        <TabsContent value="seguridad" className="mt-5">
          <TotpSection confirmed={totpConfirmed} onConfirmed={() => setTotpConfirmed(true)} />
        </TabsContent>

        {/* ── Config groups ── */}
        {groups.map(group => {
          const meta        = GROUP_META[group]
          const groupRows   = rows.filter(r => r.group === group)
          const hasEditable = groupRows.some(r => r.editable)
          const isSaving    = savingGroup === group
          const isPrices    = group === 'prices'

          return (
            <TabsContent key={group} value={group} className="mt-5">
              <section className="flex flex-col gap-3">
                {meta?.description && (
                  <p className="text-text-muted text-xs">{meta.description}</p>
                )}

                <div className="bg-surface border border-border rounded-2xl p-5">
                  {isPrices && <PriceTable rows={rows} />}

                  <div className="flex flex-col">
                    {groupRows.map(row => (
                      <ConfigField
                        key={row.key}
                        row={row}
                        value={values[row.key] ?? row.value}
                        error={errors[row.key]}
                        onChange={handleChange}
                      />
                    ))}
                  </div>

                  {hasEditable && (
                    <div className="pt-4 border-t border-border mt-3 flex justify-end">
                      <button
                        onClick={() => handleSaveGroup(group)}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-5 py-2.5 bg-accent text-background text-sm font-bold rounded-xl hover:bg-accent-hover transition-colors disabled:opacity-50 cursor-pointer"
                      >
                        {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {isSaving ? 'Guardando…' : 'Guardar cambios'}
                      </button>
                    </div>
                  )}
                </div>
              </section>
            </TabsContent>
          )
        })}
      </Tabs>

      {totpModal && (
        <TotpVerifyModal
          actionLabel="Verificar y guardar"
          onVerified={handleTotpVerified}
          onCancel={() => setTotpModal(null)}
        />
      )}
    </div>
  )
}
